import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  getGetApiAgentsAgentIdQueryKey,
  useGetApiAgentsAgentId,
  usePatchApiAgentsAgentId,
} from "@/lib/api/generated/agents/agents";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import {
  promptDocumentWrite,
  readAgentPromptDocument,
  type ControlPlaneAgentPromptKey,
  type ControlPlaneAgentRecord,
} from "@/lib/types/control-plane-agents";
import { toastMutationError } from "@/lib/utils/formatting";
import { useAgentEditorDirty, type AgentEditorTabId } from "@/stores/agent-editor-ui-store";
import { AgentTabSurface } from "./agent-tab-surface";

type AgentPromptDocumentTabProps = {
  tabId: AgentEditorTabId;
  promptKey: ControlPlaneAgentPromptKey;
  title: string;
  placeholder: string;
};

export function AgentPromptDocumentTab({
  tabId,
  promptKey,
  title,
  placeholder,
}: AgentPromptDocumentTabProps) {
  const { agentId } = useParams<{ agentId: string }>();
  const { tenantId } = useTenantContext();
  const queryClient = useQueryClient();
  const { data: agent } = useGetApiAgentsAgentId<ControlPlaneAgentRecord>(agentId!, {
    query: { enabled: Boolean(tenantId && agentId) },
  });
  const mutation = usePatchApiAgentsAgentId();
  const savedValue = readAgentPromptDocument(agent, promptKey);
  const [value, setValue] = useState(savedValue);

  useEffect(() => {
    setValue(savedValue);
  }, [savedValue]);

  const isDirty = value !== savedValue;
  useAgentEditorDirty(tabId, isDirty);

  const handleSave = async () => {
    if (!agent || !agentId || !tenantId) {return;}

    try {
      const updated = await mutation.mutateAsync({
        agentId,
        data: {
          tenantId,
          promptDocuments: [promptDocumentWrite(promptKey, value)],
        },
      });
      queryClient.setQueryData(
        getGetApiAgentsAgentIdQueryKey(agentId),
        updated as ControlPlaneAgentRecord,
      );
      toast.success(`${promptKey} saved`);
    } catch (err) {
      toast.error(toastMutationError(err, `Failed to save ${title}`));
    }
  };

  const handleDiscard = () => {
    setValue(savedValue);
  };

  return (
    <AgentTabSurface
      title={title}
      actions={
        isDirty ? (
          <>
            <Button variant="ghost" size="sm" onClick={handleDiscard}>
              Discard
            </Button>
            <Button size="sm" onClick={handleSave} disabled={mutation.isPending}>
              {mutation.isPending ? "Saving..." : "Save"}
            </Button>
          </>
        ) : null
      }
    >
      <div>
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="min-h-[28rem] w-full resize-y rounded-[var(--radius-md)] bg-[var(--bg-elevated)] p-4 font-mono text-sm leading-6 outline-none transition-[box-shadow] focus:ring-2 focus:ring-[var(--accent)]/15"
          style={{ color: "var(--text)" }}
          placeholder={placeholder}
        />
      </div>
    </AgentTabSurface>
  );
}
